import errorHandler from './utils/errorHandler';
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const savedUser = localStorage.getItem('user');
            return savedUser ? JSON.parse(savedUser) : null;
        } catch (error) {
            console.error('Error parsing stored user:', error);
            return null;
        }
    });
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const navigate = useNavigate();

    // Helper to safely store tokens
    const storeAuthData = (accessToken, refreshToken, userData) => {
        if (!accessToken || !refreshToken || !userData) {
            console.error('Missing required auth data');
            return false;
        }

        try {
            // Store tokens separately to avoid concatenation
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('refresh_token', refreshToken);
            localStorage.setItem('user', JSON.stringify(userData));

            // Update API headers
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            return true;
        } catch (error) {
            console.error('Error storing auth data:', error);
            return false;
        }
    };

    // Function to fetch user account data - FIXED: Don't overwrite email
    const fetchUserProfile = useCallback(async () => {
        try {
            const response = await api.get('/user/account');
            errorHandler.logDebug('AuthContext', 'User account response:', response.data);
            
            if (response.data && response.data.account_id) {
                // Get current user data from state (more reliable than localStorage)
                const currentUser = user || {};
                
                // CRITICAL FIX: Never overwrite email - only update safe fields
                const updatedUserData = { 
                    ...currentUser, 
                    account_id: response.data.account_id,
                    role: response.data.role || currentUser.role,
                    user_id: response.data.user_id || currentUser.user_id
                    // EMAIL IS DELIBERATELY NOT INCLUDED - keep original from login
                };
                
                // Store the updated user data
                localStorage.setItem('user', JSON.stringify(updatedUserData));
                
                // Update state
                setUser(updatedUserData);
                
                console.log('Account ID updated (email preserved):', {
                    old_account_id: currentUser.account_id,
                    new_account_id: response.data.account_id,
                    email_preserved: currentUser.email,
                    user_id: response.data.user_id
                });
                
                return updatedUserData;
            }
            return user;
        } catch (error) {
            console.error('Error fetching user account:', error);
            return user;
        }
    }, [user]);

    // SSO Login Handler
    const handleSSOLogin = useCallback(async () => {
        try {
            // Check if there's an SSO token in the URL or session
            const urlParams = new URLSearchParams(window.location.search);
            const ssoToken = urlParams.get('token');
            
            // Or check if SSO session data exists (set by your Flask route)
            const ssoAuthData = sessionStorage.getItem('sso_auth_data');
            
            if (ssoToken || ssoAuthData) {
                setIsLoggingIn(true);
                console.log('SSO authentication detected, processing...');
                
                let authData;
                if (ssoAuthData) {
                    // Parse SSO data set by Flask backend
                    authData = JSON.parse(ssoAuthData);
                    sessionStorage.removeItem('sso_auth_data'); // Clean up
                    console.log('Using SSO session data');
                } else if (ssoToken) {
                    // Use token from URL parameter
                    authData = {
                        access_token: ssoToken,
                        user: JSON.parse(urlParams.get('user') || '{}')
                    };
                    console.log('Using SSO URL token');
                }
                
                if (authData && authData.access_token) {
                    // Clear any existing auth data first
                    localStorage.clear();
                    delete api.defaults.headers.common['Authorization'];
                    setUser(null);
                    
                    // Store the authentication data
                    localStorage.setItem('access_token', authData.access_token);
                    if (authData.refresh_token) {
                        localStorage.setItem('refresh_token', authData.refresh_token);
                    }
                    
                    // Set API headers
                    api.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;
                    
                    // Start with the user data from SSO
                    let userData = authData.user || {};
                    
                    // Fetch complete user profile to get account_id
                    try {
                        const accountResponse = await api.get('/user/account');
                        console.log('SSO Account response:', accountResponse.data);
                        
                        if (accountResponse.data && accountResponse.data.account_id) {
                            userData = {
                                ...userData,
                                account_id: accountResponse.data.account_id,
                                role: accountResponse.data.role || userData.role,
                                user_id: accountResponse.data.user_id || userData.user_id
                            };
                        }
                    } catch (accountError) {
                        console.error('Error fetching account during SSO:', accountError);
                        // Continue with SSO login without account_id
                    }
                    
                    // Store user data and update state
                    localStorage.setItem('user', JSON.stringify(userData));
                    setUser(userData);
                    
                    // Clean up URL parameters
                    window.history.replaceState({}, document.title, window.location.pathname);
                    
                    // Navigate to dashboard
                    console.log('SSO login successful, redirecting to dashboard. User:', userData);
                    navigate('/dashboard', { replace: true });
                    
                    return { success: true, user: userData };
                }
            }
            
            return { success: false, error: 'No SSO data found' };
            
        } catch (error) {
            console.error('SSO login error:', error);
            // Clean up any partial auth data
            localStorage.clear();
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            return { success: false, error: error.message };
        } finally {
            setIsLoggingIn(false);
        }
    }, [navigate]);

    // Check for SSO authentication on component mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ssoToken = urlParams.get('token');
        const ssoAuthData = sessionStorage.getItem('sso_auth_data');
        
        // Only attempt SSO if user is not already authenticated
        if ((ssoToken || ssoAuthData) && !user) {
            console.log('SSO data detected on page load, attempting SSO login...');
            handleSSOLogin();
        }
    }, [handleSSOLogin, user]);

    const login = useCallback(async (email, password) => {
        setIsLoggingIn(true);
        try {
            // CRITICAL: Clear ALL localStorage to remove stale account_id
            localStorage.clear();
            delete api.defaults.headers.common['Authorization'];

            // Force clear any cached user state
            setUser(null);

            const response = await api.post('/auth/login', { email, password });
            console.log('Login response:', {
                status: response.status,
                hasAccessToken: !!response.data?.access_token,
                hasRefreshToken: !!response.data?.refresh_token,
                hasUser: !!response.data?.user
            });

            if (!response.data?.access_token || !response.data?.refresh_token) {
                throw new Error('Invalid authentication response');
            }

            // Get initial user data - PRESERVE the login email
            let userData = response.data.user || {};
            
            // CRITICAL: Ensure we preserve the login email
            if (!userData.email) {
                userData.email = email; // Use the email they logged in with
            }
            
            // Set the token to make authorized requests
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
            
            // Store tokens first (without user data)
            localStorage.setItem('access_token', response.data.access_token);
            localStorage.setItem('refresh_token', response.data.refresh_token);

            // CRITICAL: Fetch account_id BEFORE storing user data
            try {
                const accountResponse = await api.get('/user/account');
                console.log('Account response during login:', accountResponse.data);
                
                if (accountResponse.data && accountResponse.data.account_id) {
                    // Build complete user data with fresh account_id
                    // FIXED: NEVER overwrite email - keep the original from login
                    userData = { 
                        ...userData,  // This includes the preserved login email
                        account_id: accountResponse.data.account_id,
                        role: accountResponse.data.role || userData.role,
                        user_id: accountResponse.data.user_id || userData.user_id
                        // EMAIL IS DELIBERATELY NOT OVERWRITTEN
                    };
                    
                    console.log('Complete user data with account_id (email preserved):', {
                        ...userData,
                        email_source: 'login_preserved'
                    });
                } else {
                    console.warn('No account_id received from /user/account endpoint');
                }
            } catch (accountError) {
                console.error('Error fetching user account during login:', accountError);
                // Continue with login without the account_id
            }

            // NOW store the complete user data with account_id
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);

            // Navigate to the main dashboard
            console.log('Redirecting user to main dashboard. Role:', userData.role, 'Email:', userData.email);
            navigate('/dashboard', { replace: true });
            
            return { success: true, user: userData };

        } catch (error) {
            console.error('Login error:', error);
            // Clean up any partial auth data
            localStorage.clear();
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            return {
                success: false,
                error: error.response?.data?.error || error.message || 'Login failed'
            };
        } finally {
            setIsLoggingIn(false);
        }
    }, [navigate]);

    const logout = useCallback(() => {
        try {
            localStorage.clear();
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Logout error:', error);
        }
    }, [navigate]);

    const checkAuth = useCallback(() => {
        const accessToken = localStorage.getItem('access_token');
        const userData = localStorage.getItem('user');
        
        if (!accessToken || !userData) {
            logout();
            return false;
        }
        
        try {
            const parsedUser = JSON.parse(userData);
            
            // If authenticated but missing account_id, try to fetch it
            if (!parsedUser.account_id) {
                console.log('Missing account_id, fetching user profile...');
                fetchUserProfile();
            }
            
            return true;
        } catch (error) {
            console.error('Error parsing user data:', error);
            logout();
            return false;
        }
    }, [logout, fetchUserProfile]);

    // Helper function to force refresh account_id - FIXED: Don't overwrite email
    const refreshAccountId = useCallback(async () => {
        try {
            const response = await api.get('/user/account');
            if (response.data && response.data.account_id) {
                const currentUser = user || {};
                // CRITICAL FIX: Never overwrite email - only update safe fields
                const updatedUserData = { 
                    ...currentUser, 
                    account_id: response.data.account_id,
                    role: response.data.role || currentUser.role,
                    user_id: response.data.user_id || currentUser.user_id
                    // EMAIL IS DELIBERATELY NOT INCLUDED - keep original
                };
                
                localStorage.setItem('user', JSON.stringify(updatedUserData));
                setUser(updatedUserData);
                
                console.log('Account ID force refreshed (email preserved):', {
                    account_id: updatedUserData.account_id,
                    email_preserved: currentUser.email
                });
                return updatedUserData.account_id;
            }
        } catch (error) {
            console.error('Error refreshing account ID:', error);
        }
        return null;
    }, [user]);

    return (
        <AuthContext.Provider value={{ 
            user, 
            login, 
            logout, 
            isLoggingIn,
            isAuthenticated: !!user && checkAuth(),
            refreshUserProfile: fetchUserProfile,
            refreshAccountId: refreshAccountId,
            handleSSOLogin
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;